import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/ErrorBoundary';
import logger from './utils/logger';
import axios from 'axios';
import { getApiUrl, configureAxios } from './utils/apiConfig';

// Reset logs on page load/refresh
const resetClientLogs = async () => {
  try {
    // Direct XMLHttpRequest to handle CORS issues
    const xhr = new XMLHttpRequest();
    const apiUrl = getApiUrl();
    xhr.open('POST', `${apiUrl}/api/logs/reset`, false); // synchronous
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send();
    
    if (xhr.status === 200) {
    
    } else {
      console.error('Failed to reset client logs:', xhr.statusText);
    }
  } catch (error) {
    console.error('Failed to reset client logs:', error);
  }
};

// Reset logs when the page loads
resetClientLogs();

// Initialize logger
logger.configure({
  // Set log level based on environment
  minLevel: process.env.NODE_ENV === 'production' ? 'info' : 'debug',
  // Enable console output in development
  consoleOutput: process.env.NODE_ENV !== 'production',
  // Override console methods to capture all logs
  overrideConsole: true
});

// Write a test log directly to verify the logging system works
try {
  // Use XMLHttpRequest for direct logging test (not using the logger)
  const xhr = new XMLHttpRequest();
  const apiUrl = getApiUrl();
  xhr.open('POST', `${apiUrl}/api/logs/client`, false); // synchronous
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.send(JSON.stringify({
    level: 'info',
    message: 'React app startup - DIRECT TEST LOG',
    timestamp: new Date().toISOString(),
    source: 'index.js direct test'
  }));
  
  if (xhr.status === 200) {
  
  } else {
    console.error('Failed to send test log:', xhr.statusText);
  }
} catch (error) {
  console.error('Failed to send test log:', error);
}

// Now send a log through the logger system
logger.info('React app startup test log via logger system', {
  source: 'index.js logger test',
  testProperty: true
});

// Set default base URL for axios with dynamic API detection
configureAxios(axios);

// Add request/response logging for Axios
axios.interceptors.request.use(
  config => {
    logger.debug('API Request', {
      method: config.method.toUpperCase(),
      url: config.url,
      data: config.data
    });
    return config;
  },
  error => {
    logger.error('API Request Error', { 
      error: error.message,
      stack: error.stack
    });
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    logger.debug('API Response', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  error => {
    const errorData = {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      response: error.response?.data
    };
    
    logger.error('API Response Error', errorData);
    return Promise.reject(error);
  }
);

// Add window error handler
window.onerror = (message, source, lineno, colno, error) => {
  logger.error('Uncaught Error', {
    message,
    source,
    lineno,
    colno,
    stack: error?.stack
  });
};

// Add unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection', {
    reason: event.reason,
    message: event.reason?.message,
    stack: event.reason?.stack
  });
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Use our logger for performance reporting
reportWebVitals((metric) => {
  // logger.debug('Web Vitals', metric);
});
